import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import TextView from "../../../TextView";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IyugaTooltip from "../../../IyugaTooltip";
import { useDispatch, useSelector } from "react-redux";
import {
  getWheelAutoResizing,
  getWheelSegments,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  pushToHistoryAction,
  setAutomaticResizing,
  setWheelSegments,
} from "../../../../redux/reducers/segmentBuilder";
import Switch from "@mui/material/Switch";
import SegmentResizingCard from "./SegmentReisizingCard";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { SidePaneContent } from "../../Shared/DefaultSidePane";
import UndoIcon from "@mui/icons-material/Undo";

export function redistributeProportionally(availableValue, unlockedSegments) {
  const totalValue = unlockedSegments.reduce((acc, seg) => acc + seg.value, 0);
  return unlockedSegments.map((seg) =>
    Math.max(1, (seg.value / totalValue) * availableValue)
  );
}

export const DATA_MULTIPLIER = 1.01;

// Add this function before the component
const areSegmentsEqual = (segments, toggleValue) => {
  if (!segments.length) return true;

  if (toggleValue === "percentage") {
    const unlockedSegments = segments.filter((s) => !s.resize_lock);
    if (unlockedSegments.length <= 1) return true;

    // Check if all values are within 0.02 of each other
    const values = unlockedSegments.map((s) => s.value);
    const max = Math.max(...values);
    const min = Math.min(...values);

    // If difference between max and min is less than 0.02, consider them equal
    return max - min <= 0.02;
  } else {
    // For data value mode
    const firstValue = segments[0].data_value;
    return segments.every((s) => s.data_value === firstValue);
  }
};

const SegmentsTab = () => {
  const dispatch = useDispatch();

  const [toggleValue, setToggleValue] = useState("percentage");

  const wheelData = useSelector(getWheelSegments);
  const automaticResizing = useSelector(getWheelAutoResizing);

  const [segments, setSegments] = useState([]);

  useEffect(() => {
    setSegments(wheelData);
  }, [wheelData]);

  const handleSliderChange = (segmentId, newValue) => {
    const currentSegment = segments.find((s) => s.id === segmentId);
    if (!currentSegment) return;

    newValue = Number(Number(newValue).toFixed(2));
    const diff = newValue - currentSegment.value;

    // Gather all unlocked except the current one
    const unlockedSegments = segments.filter(
      (s) => !s.resize_lock && s.id !== segmentId
    );
    if (unlockedSegments.length === 0) return;

    // Distribute negative/positive difference proportionally among unlocked
    const sortedBars = [...unlockedSegments].sort((a, b) => b.value - a.value);
    const totalUnlockedValue = sortedBars.reduce(
      (sum, sb) => sum + sb.value,
      0
    );

    const updatedSegments = segments.map((seg) => {
      if (seg.id === segmentId) {
        return { ...seg, value: newValue };
      }
      if (!seg.resize_lock) {
        const proportion = seg.value / totalUnlockedValue;
        const adjustment = -diff * proportion;
        const newVal = Number(Math.max(1, seg.value + adjustment).toFixed(2));
        return { ...seg, value: newVal };
      }
      return seg;
    });

    let totalData = segments.reduce((sum, seg) => sum + seg.data_value, 0);

    const finalSegments = updatedSegments.map((seg) => {
      // each data_value is scaled by the fraction seg.value/100
      const newDataValue = (seg.value / 100) * totalData * DATA_MULTIPLIER;
      return { ...seg, data_value: Math.max(1, Math.ceil(newDataValue)) };
    });

    handleSegmentsChange(finalSegments);
  };

  const handleLockToggle = (id) => {
    let targetSegment;
    const updatedSegments = segments.map((sb) => {
      if (sb.id === id) {
        const updated = { ...sb, resize_lock: !sb.resize_lock };
        targetSegment = updated;
        return updated;
      }
      return sb;
    });

    if (!targetSegment.resize_lock) {
      const unlockedSegments = updatedSegments.filter((sb) => !sb.resize_lock);
      const lockedValue = updatedSegments.reduce(
        (sum, sb) => sum + (sb.resize_lock ? sb.value : 0),
        0
      );

      const availableValue = 100 - lockedValue;
      const distributedValues = redistributeProportionally(
        availableValue,
        unlockedSegments
      );

      let idx = 0;
      updatedSegments.forEach((sb) => {
        if (!sb.resize_lock) {
          sb = { ...sb, value: distributedValues[idx++] };
        }
      });
    }

    handleSegmentsChange(updatedSegments);
  };

  const unlockedSegments = segments.filter((s) => !s.resize_lock);
  const lockedSegments = segments.filter((s) => s.resize_lock);

  const handleSegmentsChange = (newSegments) => {
    dispatch(setWheelSegments(newSegments));
    setSegments(newSegments);
    dispatch(pushToHistoryAction());
  };

  const moveCard = (dragIndex, hoverIndex) => {
    const updated = [...segments];
    const [removed] = updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, removed);

    // Update positions based on new order
    const updatedWithPositions = updated.map((segment, index) => ({
      ...segment,
      position: (index + 1) * 10, // Positions 10, 20, 30, etc.
    }));

    // Sort by position before updating
    const sortedSegments = [...updatedWithPositions].sort(
      (a, b) => a.position - b.position
    );

    handleSegmentsChange(sortedSegments);
  };

  function handleDataValueChange(segmentId, typedNum) {
    // 1) Update that segment’s .data_value
    const updated = segments.map((s) => {
      if (s.id === segmentId) {
        return { ...s, data_value: typedNum };
      }
      return s;
    });

    const totalData = updated.reduce((sum, seg) => sum + seg.data_value, 0);

    // Convert each dataValue to a percentage
    const finalSegments = updated.map((s) => {
      const pct = (s.data_value / totalData) * 100;
      return { ...s, value: Number(Math.max(1, pct).toFixed(2)) };
    });

    handleSegmentsChange(finalSegments);
  }

  const handleUnlockAll = () => {
    const updated = segments.map((s) => ({ ...s, resize_lock: false }));
    handleSegmentsChange(updated);
  };

  const handleEqualizeAll = () => {
    let updated = [...segments];

    if (toggleValue === "percentage") {
      const lockedValue = updated.reduce(
        (sum, seg) => (seg.resize_lock ? sum + seg.value : sum),
        0
      );

      const leftover = 100 - lockedValue;

      const unlocked = updated.filter((s) => !s.resize_lock);
      if (!unlocked.length) return; // if none are unlocked, do nothing

      const equalPct = leftover / unlocked.length;

      updated = updated.map((s) => {
        return s.resize_lock
          ? s
          : { ...s, data_value: 1, value: Math.max(1, equalPct) };
      });
    } else {
      // "data" mode => set each unlocked .data_value=1 => recalc
      updated = updated.map((s) => {
        return { ...s, data_value: 1 };
      });
      // sum the data
      const totalData = updated.reduce((sum, seg) => sum + seg.data_value, 0);
      // recalc each unlocked segment's percentage
      updated = updated.map((s) => {
        const pct = (s.data_value / totalData) * 100;
        return { ...s, value: Math.max(1, pct) };
      });
    }

    handleSegmentsChange(updated);
  };

  const handleTogglePercentage = (e) => {
    const newValue = e.target.checked ? "data" : "percentage";
    setToggleValue(newValue);

    if (newValue === "data") {
      handleUnlockAll();
    }
  };

  const handleEnableAutoResizing = () => {
    const newValue = !automaticResizing;
    dispatch(setAutomaticResizing(newValue));

    if (newValue) {
      handleUnlockAll();
    }
  };

  // Inside the component, add this before the return statement
  const isAlreadyEqual = areSegmentsEqual(segments, toggleValue);

  return wheelData.length > 0 ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      {/* Automatic Resizing */}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            justifyContent: "space-between",
            paddingRight: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "16px",
            }}
          >
            <TextView variant="subtitle"> Automatic Resizing</TextView>
            <IyugaTooltip
              tooltip={"Auto resize these awesome seggy-ments ;)"}
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  color: "#123051",
                  cursor: "pointer",
                }}
              />
            </IyugaTooltip>
          </Box>

          {/* <UndoIcon sx={{ color: "#6C1FB8", cursor: "pointer" }} /> */}
        </Box>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Switch
            checked={automaticResizing}
            onChange={handleEnableAutoResizing}
            sx={{
              "& .Mui-checked": {
                color: "#9B5AF8 !important",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#9B5AF8 !important",
              },
            }}
          />
          <TextView variant="subtitle">
            {" "}
            Auto size according to content{" "}
          </TextView>
        </div>
      </Box>

      {/* Manual Resizing */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            <TextView variant="subtitle"> Manual Resizing</TextView>
            <IyugaTooltip
              tooltip={"Manually resize these awesome seggy-ments ;)"}
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  color: "#123051",
                  cursor: "pointer",
                }}
              />
            </IyugaTooltip>
          </Box>
        </Box>

        <div
          style={{
            pointerEvents: automaticResizing ? "none" : "auto",
            opacity: automaticResizing ? 0.5 : 1,
          }}
        >
          Percentage
          <Switch
            checked={toggleValue === "data"}
            onChange={handleTogglePercentage}
            disabled={automaticResizing}
            sx={{
              "& .MuiButtonBase-root": {
                color: "#9B5AF8 !important",
              },
              "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                backgroundColor: "#9B5AF8 !important",
              },
              "& .Mui-checked": {
                color: "#9B5AF8 !important",
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#9B5AF8 !important",
              },
            }}
          />
          Data Value
        </div>

        <DndProvider backend={HTML5Backend}>
          {segments.map((segment, index) => (
            <SegmentResizingCard
              key={segment.id}
              segment={segment}
              index={index}
              moveCard={moveCard}
              onValueChange={handleSliderChange}
              toggleValue={toggleValue}
              onLockToggle={handleLockToggle}
              allSegments={segments}
              onDataValueChange={handleDataValueChange}
              isAutoResizeEnabled={automaticResizing}
            />
          ))}
        </DndProvider>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            zIndex: 1,
            py: 3,
            pointerEvents: automaticResizing ? "none" : "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {toggleValue === "percentage" && (
              <TextView
                variant="subtitle"
                style={{
                  color: "#9B5AF8",
                  cursor: "pointer",
                  textDecoration: "underline",
                  opacity: lockedSegments.length === 0 && 0.5,
                  pointerEvents: lockedSegments.length === 0 && "none",
                }}
                onClick={handleUnlockAll}
              >
                Unlock All
              </TextView>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <TextView
                variant="subtitle"
                style={{
                  color: "#9B5AF8",
                  opacity:
                    (unlockedSegments.length < 2 &&
                      toggleValue === "percentage") ||
                    isAlreadyEqual
                      ? 0.5
                      : 1,
                  cursor: "pointer",
                  textDecoration: "underline",
                  pointerEvents:
                    (unlockedSegments.length < 2 &&
                      toggleValue === "percentage") ||
                    isAlreadyEqual
                      ? "none"
                      : "auto",
                }}
                onClick={handleEqualizeAll}
              >
                {" "}
                Equalize All
              </TextView>
              <IyugaTooltip
                tooltip={"Adjust all unlocked Segments to be equal in size"}
                placement="right"
              >
                <InfoOutlinedIcon
                  sx={{
                    color: "#BBBBBB",
                    cursor: "pointer",
                    width: "20px",
                    height: "20px",
                  }}
                />
              </IyugaTooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  ) : (
    <SidePaneContent />
  );
};

export default SegmentsTab;
